import { useEffect, useState } from 'react';
import {
  apiClient,
  type AllProjectsRow,
  type RoutingRule,
  type RoutingRuleAction,
  ApiError,
} from '@/lib/api/client';
import { RuleEditor } from './RuleEditor';
import { cn } from '@/lib/utils';

interface Props {
  sourceId: string;
  sourceName: string;
  onBack: () => void;
}

const ACTION_LABELS: Record<RoutingRuleAction, string> = {
  route_to_project: 'Route to project',
  create_task: 'Auto-create task',
  assign_only: 'Assign to user',
  silent_log: 'Silent log',
  escalate: 'Escalate (urgent)',
  drop: 'Drop',
};

export function RulesView({ sourceId, sourceName, onBack }: Props) {
  const [rules, setRules] = useState<RoutingRule[] | null>(null);
  const [projects, setProjects] = useState<AllProjectsRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ kind: 'new' } | { kind: 'edit'; rule: RoutingRule } | null>(
    null,
  );

  async function refresh() {
    try {
      const [r, p] = await Promise.all([
        apiClient.listRules(sourceId),
        apiClient.allProjects(),
      ]);
      setRules(r.rules);
      setProjects(p.projects);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  async function toggle(rule: RoutingRule) {
    await apiClient.updateRule(rule.rule_id, { enabled: !rule.enabled });
    refresh();
  }
  // Two-step inline confirm (matches SourcesView.destroy pattern).
  // First click sets pendingDelete + 3s auto-revert; second click within
  // the window commits. No native confirm() → no keyboard trap.
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  async function remove(rule: RoutingRule) {
    if (pendingDelete !== rule.rule_id) {
      setPendingDelete(rule.rule_id);
      setTimeout(() => {
        setPendingDelete((curr) => (curr === rule.rule_id ? null : curr));
      }, 3000);
      return;
    }
    setPendingDelete(null);
    await apiClient.deleteRule(rule.rule_id);
    refresh();
  }
  async function move(rule: RoutingRule, dir: -1 | 1) {
    if (!rules) return;
    const idx = rules.findIndex((r) => r.rule_id === rule.rule_id);
    const swap = idx + dir;
    if (swap < 0 || swap >= rules.length) return;
    const newOrder = [...rules];
    [newOrder[idx], newOrder[swap]] = [newOrder[swap]!, newOrder[idx]!];
    await apiClient.reorderRules(
      sourceId,
      newOrder.map((r) => r.rule_id),
    );
    refresh();
  }

  return (
    <main className="min-h-dvh bg-background text-foreground antialiased">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-ring/60 hover:text-foreground"
            >
              ← Back
            </button>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Routing rules · {sourceName}
            </div>
          </div>
          <button
            onClick={() => setEditing({ kind: 'new' })}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
          >
            New rule
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="space-y-1">
          <h1 className="text-balance text-3xl font-semibold tracking-tight md:text-4xl">
            Routing rules
          </h1>
          <p className="font-mono text-xs text-muted-foreground">
            Auto-classify events from <span className="text-foreground">{sourceName}</span>. Rules
            are tried in order (top-down). First match wins.
          </p>
        </div>
        {err && (
          <div role="alert" className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="mt-6 space-y-2">
          {rules === null ? (
            <div className="font-mono text-xs text-muted-foreground">Loading…</div>
          ) : rules.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/40 px-5 py-12 text-center">
              <div className="text-base text-foreground">No rules yet</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Add a rule to auto-route events from this source.
              </div>
            </div>
          ) : (
            rules.map((rule, i) => (
              <div
                key={rule.rule_id}
                className={cn(
                  'flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3',
                  !rule.enabled && 'opacity-50',
                )}
              >
                <div className="flex flex-col gap-1 font-mono text-[10px] text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => move(rule, -1)}
                    disabled={i === 0}
                    className="rounded border border-border px-1.5 py-0.5 hover:border-ring/60 disabled:opacity-30"
                    aria-label={`Move "${rule.name}" up — currently priority ${rule.priority}`}
                  >
                    <span aria-hidden>↑</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => move(rule, 1)}
                    disabled={i === rules.length - 1}
                    className="rounded border border-border px-1.5 py-0.5 hover:border-ring/60 disabled:opacity-30"
                    aria-label={`Move "${rule.name}" down — currently priority ${rule.priority}`}
                  >
                    <span aria-hidden>↓</span>
                  </button>
                </div>
                <button
                  onClick={() => setEditing({ kind: 'edit', rule })}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground">
                      #{rule.priority}
                    </span>
                    <span className="truncate font-semibold text-foreground">{rule.name}</span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                    {ACTION_LABELS[rule.action]}
                    {rule.match_count > 0 && ` · matched ${rule.match_count}×`}
                  </div>
                </button>
                <button
                  onClick={() => toggle(rule)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs',
                    rule.enabled
                      ? 'border-primary/60 text-primary'
                      : 'border-border text-muted-foreground',
                  )}
                >
                  {rule.enabled ? 'On' : 'Off'}
                </button>
                <button
                  onClick={() => remove(rule)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs transition-colors',
                    pendingDelete === rule.rule_id
                      ? 'border-destructive/70 bg-destructive/15 text-destructive'
                      : 'border-border text-muted-foreground hover:border-destructive/60 hover:text-destructive',
                  )}
                  aria-describedby={pendingDelete === rule.rule_id ? `rule-${rule.rule_id}-confirm` : undefined}
                >
                  {pendingDelete === rule.rule_id ? 'Confirm · 3s' : 'Delete'}
                </button>
                {pendingDelete === rule.rule_id && (
                  <span
                    id={`rule-${rule.rule_id}-confirm`}
                    role="status"
                    className="sr-only"
                  >
                    Click again within 3 seconds to confirm delete of {rule.name}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      {editing && (
        <RuleEditor
          mode={editing.kind === 'new' ? 'create' : 'edit'}
          rule={editing.kind === 'edit' ? editing.rule : undefined}
          sourceId={sourceId}
          projects={projects}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </main>
  );
}
